import React, { FunctionComponent, useEffect, useMemo, useRef } from 'react';
import { useStore } from 'effector-react';
import { Empty, Pagination, Spin } from 'antd';
import css from './SpamStatusesList.module.less';
import spamStatuses, {
  clearPrefetchedPage,
  DEFAULT_SPAM_STATUSES_FILTERS,
  getList,
  prefetchPage,
  resetFastPrefetch,
} from '@stores/spamStatuses';
import SpamStatusesCard from '@components/SpamStatusesCard';
import { pageSizeOptions } from '@constants/pagination';
import IStatus from '@interfaces/IStatus';
import localeWeightStore, { LOCALE } from '@stores/localeWeight';

interface SpamStatusesListProps { }

const SpamStatusesList: FunctionComponent<SpamStatusesListProps> = () => {
  const {
    list,
    preloaders,
    setPagination,
    pagination,
    filters,
    prefetchedPage,
    prefetchedPageRequest,
    shouldPrefetchNextPageFast,
  } = useStore(spamStatuses);
  const locale = useStore(localeWeightStore);
  const prefetchTimeoutRef = useRef<number | null>(null);

  const totalPages = useMemo(() => {
    if (!pagination.total || !pagination.pageSize) {
      return 0;
    }

    return Math.ceil(pagination.total / pagination.pageSize);
  }, [pagination.total, pagination.pageSize]);

  const nextPage = useMemo(() => {
    const calculatedNextPage = (pagination.current || 1) + 1;
    return totalPages && calculatedNextPage <= totalPages ? calculatedNextPage : null;
  }, [pagination.current, totalPages]);

  const expectedNextPageSize = useMemo(() => {
    if (!nextPage || !pagination.pageSize || !pagination.total) {
      return 0;
    }

    const nextPageOffset = (nextPage - 1) * pagination.pageSize;
    return Math.min(pagination.pageSize, Math.max(pagination.total - nextPageOffset, 0));
  }, [nextPage, pagination.pageSize, pagination.total]);

  const currentCacheKey = useMemo(() => {
    const localeValue = locale === LOCALE.ALL ? undefined : locale;

    return JSON.stringify({
      ...DEFAULT_SPAM_STATUSES_FILTERS,
      ...filters,
      locale: localeValue,
      pageSize: pagination.pageSize,
    });
  }, [filters, locale, pagination.pageSize]);

  const prefetchDelay = shouldPrefetchNextPageFast ? 300 : 5000;

  useEffect(() => {
    if (prefetchTimeoutRef.current) {
      window.clearTimeout(prefetchTimeoutRef.current);
    }

    if (preloaders.getList || prefetchedPageRequest?.isLoading) {
      return undefined;
    }

    if (!nextPage) {
      clearPrefetchedPage();
      resetFastPrefetch();
      return undefined;
    }

    const minimumCachedItems = Math.min(
      Math.ceil((pagination.pageSize || 0) / 2),
      expectedNextPageSize,
    );
    const hasEnoughCachedItems =
      prefetchedPage &&
      prefetchedPage.page === nextPage &&
      prefetchedPage.pageSize === pagination.pageSize &&
      prefetchedPage.cacheKey === currentCacheKey &&
      prefetchedPage.items.length >= minimumCachedItems;

    if (hasEnoughCachedItems) {
      resetFastPrefetch();
      return undefined;
    }

    prefetchTimeoutRef.current = window.setTimeout(() => {
      prefetchPage(nextPage);
    }, prefetchDelay);

    return () => {
      if (prefetchTimeoutRef.current) {
        window.clearTimeout(prefetchTimeoutRef.current);
      }
    };
  }, [
    currentCacheKey,
    expectedNextPageSize,
    nextPage,
    pagination.pageSize,
    prefetchedPage,
    prefetchedPageRequest,
    prefetchDelay,
    preloaders.getList,
  ]);

  const paginationChange = (current: number, pageSize?: number) => {
    const nextPageSize = pageSize || pagination.pageSize;
    clearPrefetchedPage();
    setPagination({ ...pagination, current, pageSize: nextPageSize });
    getList();
  };

  useEffect(() => {
    getList();
  }, [filters]);

  return (
    <div>
      <div className={css.paginationContainer}>
        <Pagination
          {...pagination}
          onChange={paginationChange}
          showQuickJumper
          showSizeChanger
          pageSizeOptions={pageSizeOptions}
        />
      </div>
      <Spin spinning={!!preloaders.getList}>
        {list.length ? (
          <>
            {list.map((status: IStatus) => (
              <div key={status.id}>
                <SpamStatusesCard status={status} />
              </div>
            ))}
          </>
        ) : (
          <Empty />
        )}
      </Spin>
      <div className={css.paginationContainer}>
        <Pagination
          {...pagination}
          onChange={paginationChange}
          showQuickJumper
          showSizeChanger
          pageSizeOptions={pageSizeOptions}

        />
      </div>
    </div>
  );
};

export default SpamStatusesList;
